package nl.volleyanalyzer.web.controller

import kotlinx.browser.document
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.web.base.SimpleComponent
import nl.volleyplanner.web.lib.Chart
import nl.volleyplanner.web.lib.Dropdown
import nl.volleyplanner.web.util.jsObject
import org.w3c.dom.Element
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.asList

data class ChartOptions(
    val displayLegend: Boolean = true,
    val displayYAxis: Boolean = true,
    val enableMethodSelection: Boolean = true
)

class PouleStandingsChartComponent(
    private val pouleStandings: MultiRoundPouleStandings,
    private val chartOptions: ChartOptions = ChartOptions(),
    private val coloredTeams: List<String> = emptyList()
) : SimpleComponent {
    private val supportedChartMethods = listOf(
        PositionPouleStandingsChartData(pouleStandings),
        PointsPouleStandingsChartData(pouleStandings),
        SetsWonPouleStandingsChartData(pouleStandings),
        SetQuotientPouleStandingsChartData(pouleStandings),
        PointsWonPouleStandingsChartData(pouleStandings)
    )
    private val grayChartColor = "rgb(222, 226, 230)"
    private val chartColors = listOf(
        "rgb(234, 134, 143)",    // - Pinkish red
        "rgb(110, 168, 254)",    // - Light blue
        "rgb(163, 112, 247)",    // - Violet
        "rgb(254, 178, 114)",    // - Orange
        "rgb(121, 223, 193)",    // - Mint green
        "rgb(110, 223, 246)",    // - Cyan
        "rgb(186, 160, 255)",    // - Lavender
        "rgb(255, 128, 192)",    // - Light pink
        "rgb(132, 255, 170)",    // - Soft green
        "rgb(255, 209, 102)",    // - Golden yellow
        "rgb(140, 190, 255)",    // - Light sky blue (adjusted)
        "rgb(255, 160, 102)",    // - Soft salmon (adjusted)
        "rgb(182, 233, 156)",    // - Lime green
        "rgb(255, 145, 225)",    // - Bright magenta (adjusted)
        "rgb(130, 200, 250)"    // - Teal blue (adjusted)
    )

    private val chartHtmlId = "va-poule-standings-chart-${pouleStandings.pouleCode}"
    private var _currentChart: Chart? = null
    private var teamDatasetIndex: Map<String, Int>? = null

    override fun dispose() {
        super.dispose()
        _currentChart?.destroy()
    }

    fun focusTeams(teamsToFocus: Set<String>) {
        if (teamDatasetIndex == null) return
        val dynamicChart = _currentChart.asDynamic()

        val size = dynamicChart.data.datasets.length
        // First reset all lines so that when teams are removed their width is set back to normal
        for (i in 0 until size) {
            dynamicChart.data.datasets[i].borderWidth = 3
        }
        teamsToFocus.forEach { team ->
            dynamicChart.data.datasets[teamDatasetIndex!!.getValue(team)].borderWidth = 6
        }
        _currentChart?.update()
    }

    private fun updateChart(method: PouleStandingsChartData) {
        if (pouleStandings.scoringMethod == null) {
            return
        }

        val chartElement = document.getElementById(chartHtmlId)!! as HTMLCanvasElement

        val colorScheme = if (coloredTeams.isNotEmpty()) {
            pouleStandings.teams.map { team ->
                if (coloredTeams.contains(team)) {
                    Pair(team, chartColors[coloredTeams.indexOf(team)])
                } else {
                    Pair(team, grayChartColor)
                }
            }.toMap()
        } else {
            pouleStandings.teams.mapIndexed { index, team ->
                Pair(team, chartColors[index])
            }.toMap()
        }

        val datasetsByTeam = method.generateDatasets(colorScheme)
        teamDatasetIndex = datasetsByTeam.keys.mapIndexed { index, key -> Pair(key, index) }.toMap()

        val config: dynamic = Any()
        config.type = "line"
        config.data = jsObject {
            it.labels = pouleStandings.roundStandings.keys.toTypedArray()
            it.datasets = datasetsByTeam.values.toTypedArray()
        }
        config.options = jsObject {
            it.maintainAspectRatio = false
            it.scales = jsObject {
                it.x = jsObject {
                    it.title = jsObject {
                        it.display = true
                        it.text = "# Wedstrijden gespeeld"
                    }
                    it.min = 0
                    it.max = pouleStandings.roundStandings.size
                    it.ticks = jsObject {
                        it.count = pouleStandings.roundStandings.size
                        it.stepSize = 1
                    }
                }
                it.y = method.generateYScaleConfig()
            }
        }
        if (!chartOptions.displayYAxis) {
            config.options.scales.y.display = false
        }
        config.options.plugins = jsObject {}
        if (chartOptions.displayLegend) {
            config.options.plugins.legend = jsObject {
                it.align = "start"
            }
        } else {
            config.options.plugins.legend = jsObject {
                it.display = false
            }
        }

        _currentChart?.destroy()
        _currentChart = Chart(chartElement, config)
    }

    override fun onReady() {
        super.onReady()

        updateChart(supportedChartMethods.first())
    }

    override fun render(flowContent: FlowContent) {
        val minHeight = pouleStandings.teams.size * 40
        flowContent.div {
            div {
                style = "min-height: ${minHeight}px;"
                canvas { id = chartHtmlId }
            }

            if (chartOptions.enableMethodSelection) {
                div {
                    span(classes = "me-2") {
                        +""
                    }
                    // Desktop (lg+ displays)
                    div(classes = "d-none d-lg-block btn-group btn-group-sm p-1") {
                        id = "va-chart-selector"
                        supportedChartMethods.forEach { method ->
                            button(classes = "btn btn-outline-secondary") {
                                if (method == supportedChartMethods.first()) classes += "active"
                                type = ButtonType.button

                                onClickFunction = { event ->
                                    event.preventDefault()
                                    document.getElementById("va-chart-selector")!!
                                        .querySelectorAll("button")
                                        .asList().forEach { node ->
                                            (node as Element).removeClass("active")
                                        }
                                    (event.target!! as Element).addClass("active")

                                    updateChart(method)
                                }

                                +method.name
                            }
                        }
                    }
                    // Mobile (md-) displays
                    div (classes = "d-lg-none dropdown") {
                        +"Selecteer grafiek "
                        button (classes = "btn btn-secondary dropdown-toggle") {
                            id = "va-chart-selector-dropdown-button"
                            type = ButtonType.button
                            attributes["data-bs-toggle"] = "dropdown"
                            attributes["aria-expanded"] = "false"

                            onClickFunction = { event ->
                                event.preventDefault()
                                Dropdown.getOrCreateInstance(event.target as Element)
                                    .toggle()
                            }

                            +"Positie"
                        }
                        ul (classes = "dropdown-menu") {
                            supportedChartMethods.forEach { method ->
                                li {
                                    a (classes = "dropdown-item") {
                                        href = "#"
                                        onClickFunction = { event ->
                                            event.preventDefault()

                                            val btn = document.getElementById("va-chart-selector-dropdown-button")!!
                                            btn.innerHTML = method.name

                                            updateChart(method)
                                        }
                                        +method.name
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}